import React, { useEffect, useState, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { getQuizzes, getUserProfile, createCheckoutSession } from '../../services/api';
import { trackEvent } from '../../components/Tools/analytics';
import './ExamPage.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const ExamPage = () => {
    const [quizzes, setQuizzes] = useState([]);
    const [userProfile, setUserProfile] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedQuizId, setSelectedQuizId] = useState(null);
    const [selectedQuizTitle, setSelectedQuizTitle] = useState('');
    const navigate = useNavigate();

    const fetchQuizzesAndProfile = useCallback(async () => {
        try {
            const [quizzesResponse, profileResponse] = await Promise.all([
                getQuizzes(),
                getUserProfile()
            ]);
            setQuizzes(quizzesResponse.data);
            setUserProfile(profileResponse.data);
        } catch (error) {
            console.error('Lỗi khi tải dữ liệu:', error);
        }
    }, []);

    useEffect(() => {
        fetchQuizzesAndProfile();
        trackEvent('Viewed Exam Page');
    }, [fetchQuizzesAndProfile]);

    const handlePayment = async (id) => {
        try {
            const quiz = quizzes.find(q => q._id === id);
            trackEvent('Clicked Buy Exam', {
                examId: id,
                examTitle: quiz?.title || 'Unknown',
                examPrice: quiz?.price || 0,
            });

            const response = await createCheckoutSession(id);
            const stripe = await stripePromise;
            const { error } = await stripe.redirectToCheckout({ sessionId: response.sessionId });
            if (error) {
                console.error('Thanh toán Stripe thất bại:', error);
            }
        } catch (error) {
            console.error('Lỗi khi tạo phiên thanh toán:', error);
        }
    };

    const handleStartTest = (id) => {
        const quiz = quizzes.find(q => q._id === id);
        trackEvent('Clicked Start Exam', {
            examId: id,
            examTitle: quiz?.title || 'Unknown',
        });
        navigate(`/exam/${id}`);
    };

    const handleBuyClick = (id, title) => {
        setSelectedQuizId(id);
        setSelectedQuizTitle(title);
        setShowConfirmModal(true);
    };

    const confirmPayment = async () => {
        setShowConfirmModal(false);
        await handlePayment(selectedQuizId);
    };

    const canAccessQuiz = (quiz) => {
        if (quiz.price === 0) return true;
        if (userProfile?.isPremium) return true;
        return false;
    };

    return (
        <Container fluid className="exam-page-container py-5">
            <Row className='g-4'>
                <Col lg={6} md={12} className='align-items-center'>
                    <h3 className='intro-title'>Giới thiệu và Quy định của Bài Thi</h3>
                    <p>
                        Chào mừng bạn đến với trang thi thử. Trước khi bắt đầu, vui lòng đọc kỹ các quy định:
                    </p>
                    <ul>
                        <li>Bài thi gồm 70 câu hỏi, bạn có 90 phút để hoàn thành.</li>
                        <li>Bạn có thể đánh dấu các câu hỏi để xem lại sau.</li>
                        <li>Có thể quay lại các câu hỏi trước hoặc nhảy đến câu hỏi bất kỳ.</li>
                        <li>Nhấn nút "Nộp bài" để hoàn thành bài thi.</li>
                    </ul>
                    <p>
                        Vui lòng chọn một bài thi từ danh sách dưới đây và bắt đầu khi bạn đã sẵn sàng.
                    </p>
                </Col>

                <Col lg={6} md={12}>
                    <Row className='g-4'>
                        {quizzes.map((quiz) => (
                            <Col key={quiz._id} lg={6} md={12} sm={12} className="quiz-item">
                                <Card className="h-100 exam-quiz-card">
                                    <Card.Body className="d-flex flex-column">
                                        <Card.Title className="mb-3 d-flex justify-content-between align-items-center">
                                            <span>{quiz.title}</span>
                                            {quiz.price > 0 && !userProfile?.isPremium && (
                                                <Badge bg="info" pill>
                                                    ${quiz.price}
                                                </Badge>
                                            )}
                                            {userProfile?.isPremium && quiz.price > 0 && (
                                                <Badge bg="success" pill>
                                                    ${quiz.price}
                                                </Badge>
                                            )}
                                        </Card.Title>
                                        <Card.Text className="flex-grow-1">{quiz.description}</Card.Text>
                                        <div className="mt-3">
                                            {canAccessQuiz(quiz) ? (
                                                <Button
                                                    variant="primary"
                                                    onClick={() => handleStartTest(quiz._id)}
                                                    className="w-100 exam-button exam-button-primary"
                                                >
                                                    Bắt Đầu Thi
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="success"
                                                    onClick={() => handleBuyClick(quiz._id, quiz.title)}
                                                    className="w-100 exam-button exam-button-success"
                                                >
                                                    Mua Đề Thi
                                                </Button>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Col>
            </Row>

            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ color: 'white !important' }}>Xác nhận mua đề thi</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Bạn sắp mua đề thi: <strong>{selectedQuizTitle}</strong>.
                    </p>
                    <ul>
                        <li>Bài thi gồm <strong>70 câu hỏi</strong>, thời gian làm bài 90 phút.</li>
                        <li>Sau khi thanh toán, bạn sẽ được truy cập vào đề thi này.</li>
                        <li>Bạn có thể làm bài, xem đáp án và chấm điểm ngay sau khi hoàn thành.</li>
                    </ul>
                    <hr />
                    <small className="text-muted">
                        Mọi thanh toán được xử lý an toàn qua Stripe. Trang web <strong>không lưu trữ thông tin thẻ tín dụng</strong> của bạn.
                    </small>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Hủy
                    </Button>
                    <Button variant="primary" onClick={confirmPayment}>
                        Tiếp tục thanh toán
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ExamPage;
